import { useQuery } from '@apollo/client';
import { phoenixLegalCurrenciesQuery } from '@marriott/mi-legal-graphql';
import { Messages } from '@marriott/mi-ui-library';
import axios from 'axios';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { scrollToTop } from '../../utils/scrollUtils';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { BrgModelProps, FormData, PhoenixLegalGetCurrenciesListQueryResult } from './BrgClaimForm.types';
import { BrgComparisonRateInfo } from './BrgComparisonRateInfo';
import { BrgFormSubmissionControls } from './BrgFormSubmissionControls';
import { BrgGuestInfo } from './BrgGuestInfo';
import { BrgReservationInfo } from './BrgReservationInfo';
import { BrgRewardsInfo } from './BrgRewardsInfo';
// import mockData from './__mock__/phoenixLegalCurrenciesQuery.mock.json';

const GET_CURRENCIES_OPERATION_NAME = 'phoenixLegalCurrenciesQuery';
const DATE_FORMAT = 'YYYY-MM-DD';
const TEMPLATE_NAME = 'submit-brgform';

interface Props extends BrgModelProps {
  goToThankYou: () => void;
}

export function BrgForm({ goToThankYou, ...model }: Props) {
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  const {
    dropDownPlaceholder,
    submitButtonText,
    clearButtonText,
    fieldRequiredText,
    guestInformation,
    reservationInformation,
    comparisonRateInformation,
    rewardsInformation,
    agreeSubmitErrorLabel,
    agreeSubmitLabel,
    apiErrorMessage,
  } = model;

  const { handleSubmit } = useFormContext<FormData>();

  // This function will be triggered when there's an error
  const showAPIAlert = () => {
    setShowErrorAlert(true);
  };

  const onSubmit = async (formData: FormData) => {
    const emailAPIEndPoint = process.env['NEXT_PUBLIC_LEGAL_BRG_SERVICE_PATH'] ?? '/mi/v1/legal/submitBrg';
    console.info(`Email api service path is : ${emailAPIEndPoint}`);

    // Prepare the payload
    const payload = {
      ...formData,
      templateName: TEMPLATE_NAME,
      checkin: moment(formData.checkin).format(DATE_FORMAT),
      checkout: moment(formData.checkout).format(DATE_FORMAT),
    };

    try {
      await axios.post(emailAPIEndPoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language,
        },
        withCredentials: true,
      });
      goToThankYou();
    } catch (error) {
      // handle your error
      console.error(`Something went wrong while sending mail: ${JSON.stringify(error)}`);
      showAPIAlert();
      scrollToTop();
    }
  };

  const { isAuthorMode, language } = useLegalGenericStore();
  const { sessionId } = useAuthStore();

  const { data: currencyData, loading: isCurrencyDataLoading } = useQuery<PhoenixLegalGetCurrenciesListQueryResult>(
    phoenixLegalCurrenciesQuery,
    {
      variables: {
        type: 'CURRENCY_TYPES',
      },
      context: generateApolloClientHeadersForUXLCalls(sessionId, GET_CURRENCIES_OPERATION_NAME, language),
      skip: isAuthorMode !== false,
      onError: error => {
        console.error(`Something went wrong while fetching currencies: ${JSON.stringify(error)}`);
        showAPIAlert();
        scrollToTop();
      },
    }
  );

  const currencyOptions = useMemo(() => {
    const lookups = currencyData?.lookupsByType?.lookups || [];
    return lookups
      .map(({ label, code }) => ({
        label: `${label} (${code})`,
        value: `${label} (${code})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [currencyData?.lookupsByType?.lookups]);

  return (
    <div className="container">
      <div className="brg-container">
        {showErrorAlert && <Messages messageType="error-sev1" className="mb-4" messageText={apiErrorMessage} />}
        <h1 className="t-title-m t-font-weight-m">{model.pageTitle}</h1>
        <p>{model.pageFormDescription}</p>
        <div className="t-background-color legal-box-shadow brg-form-container brg-form">
          <p className="t-font-s">{fieldRequiredText}</p>
          <form onSubmit={handleSubmit(onSubmit)} className="brg-form">
            <BrgGuestInfo guestInformation={guestInformation} showAPIAlert={showAPIAlert} />
            <BrgReservationInfo
              reservationInformation={reservationInformation}
              dropDownPlaceholder={dropDownPlaceholder}
              currencyOptions={currencyOptions}
              isCurrencyDataLoading={isCurrencyDataLoading}
              showAPIAlert={showAPIAlert}
            />
            <BrgComparisonRateInfo
              comparisonRateInformation={comparisonRateInformation}
              dropDownPlaceholder={dropDownPlaceholder}
              currencyOptions={currencyOptions}
              isCurrencyDataLoading={isCurrencyDataLoading}
            />
            <Messages messageText={comparisonRateInformation.manualVerificationNoticeText!} className="mt-n5" />
            <BrgRewardsInfo rewardsInformation={rewardsInformation} />
            <BrgFormSubmissionControls
              agreeSubmitLabel={agreeSubmitLabel}
              agreeSubmitErrorLabel={agreeSubmitErrorLabel}
              submitButtonText={submitButtonText}
              clearButtonText={clearButtonText}
            />
          </form>
        </div>
      </div>
    </div>
  );
}
